import { IndicatorStore } from '../stores/indicator/indicator.state';
import {
  ActiveUserStore,
  GuestAccessConfig,
} from '../stores/user/active-user.state';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';
import { CategoryStore } from '../stores/category/category.state';
import { GlobalsStore } from '../stores/globals.state';
import { GeoDataStore } from '../stores/geo/geo-data.state';
import { BookmarkCategoryStore } from '../stores/bookmark-category/bookmark-category.state';
import { BookmarkStore } from '../stores/bookmark/bookmark.state';
import { RoleStore } from '../stores/role/role.state';
import { PermissionStore } from '../stores/permission.state';
import { getState } from '@ngrx/signals';
import { Commune } from '../models/commune';
import { RoutingService } from '../services/routing.service';
import { StoryStore } from '../stores/story/story.state';
import { NotificationStore } from '../stores/notification/notification.state';
import { ColorsetStore } from '../stores/colorset/colorset.state';
import { WorkspaceStore } from '../stores/workspace/workspace.state';
import { getFeatureAndFilterGroups } from '../stores/geo/geo-utils';
import { Bookmark } from '../models/bookmark';

@Injectable({
  providedIn: 'root',
})
export abstract class InitializationHandler {
  readonly activeUserStore = inject(ActiveUserStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);
  readonly layerStore = inject(DataLayerStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly globalStore = inject(GlobalsStore);
  readonly geoDataStore = inject(GeoDataStore);
  readonly userStore = inject(ActiveUserStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly roleStore = inject(RoleStore);
  readonly permissionStore = inject(PermissionStore);
  readonly notificationStore = inject(NotificationStore);
  readonly colorsetStore = inject(ColorsetStore);
  readonly workspaceStore = inject(WorkspaceStore);

  constructor(protected routingService: RoutingService) {
    // Once categories are initialized, fetch indicators
    effect(() => {
      if (this.areCategoriesInitialized() && this.areBookmarksInitialized()) {
        queueMicrotask(() => this.handleIndicators());
      }
    });

    // Once layer-types are initialized, fetch layers
    effect(() => {
      if (this.areLayerTypesInitialized() && this.areWorkspacesInitialized()) {
        queueMicrotask(() => this.handleGeoData());
        queueMicrotask(() => this.handleLayers());
      }
    });

    // Once indicators are initialized, fetch indicator data
    effect(() => {
      if (
        this.areCategoriesInitialized() &&
        this.areLayersInitialized() &&
        this.areGeoDataInitialized() &&
        this.areWorkspacesInitialized()
      ) {
        queueMicrotask(() => this.handleIndicatorData());
      }
    });

    // Once bookmarkCategories are initialized, fetch bookmarks data
    effect(() => {
      if (this.areBookmarkCategoriesInitialized()) {
        queueMicrotask(() => this.handleBookmarks());
      }
    });
  }

  // Shared state variables
  protected _storesInitialized = signal(false);
  protected _activeCommune = signal<Commune | null>(null);

  readonly areLayerTypesInitialized = computed(
    () => getState(this.layerTypeStore).initialized
  );

  readonly areLayersInitialized = computed(
    () => getState(this.layerStore).initialized
  );

  readonly areGeoDataInitialized = computed(
    () => getState(this.geoDataStore).initialized
  );

  readonly areCategoriesInitialized = computed(
    () => getState(this.categoryStore).initialized
  );

  readonly areBookmarksInitialized = computed(
    () => getState(this.bookmarkStore).initialized
  );

  readonly areBookmarkCategoriesInitialized = computed(
    () => getState(this.bookmarkCategoryStore).initialized
  );

  readonly areIndicatorsInitialized = computed(
    () =>
      getState(this.indicatorStore).initialized &&
      this.areCategoriesInitialized() // Depends on categories
  );

  readonly isIndicatorDataReady = computed(
    () =>
      getState(this.indicatorDataStore).initialized &&
      this.areIndicatorsInitialized() // Depends on indicators
  );

  readonly areWorkspacesInitialized = computed(
    () => getState(this.workspaceStore).initialized
  );

  /**
   * Checks if the stores have been initialized.
   *
   * @return {boolean} Returns true if the stores are initialized, otherwise false.
   */
  get storesInitialized(): boolean {
    return this._storesInitialized();
  }

  /**
   * Retrieves the active commune.
   *
   * @return {Commune | null} The currently active commune if available, otherwise null.
   */
  get activeCommune(): Commune | null {
    return this._activeCommune();
  }

  /**
   * Determines if all the necessary stores in the application have been initialized.
   *
   * @return {boolean} True if all required stores are initialized and the global store has no selected type, otherwise false.
   */
  areStoresInitialized(): boolean {
    const activeUserStore = getState(this.activeUserStore);
    const storyState = getState(this.storyStore);
    const colorsetState = getState(this.colorsetStore);
    const layerTypeState = getState(this.layerTypeStore);
    const layerState = getState(this.layerStore);
    const geoDataState = getState(this.geoDataStore);
    const indicatorState = getState(this.indicatorStore);
    const dataState = getState(this.indicatorDataStore);
    const categoryState = getState(this.categoryStore);
    const bookmarkCategoryState = getState(this.bookmarkCategoryStore);
    const bookmarkState = getState(this.bookmarkStore);
    const roleState = getState(this.roleStore);
    const notificationState = getState(this.notificationStore);

    return (
      layerTypeState.initialized &&
      layerState.initialized &&
      geoDataState.initialized &&
      indicatorState.initialized &&
      dataState.initialized &&
      categoryState.initialized &&
      bookmarkCategoryState.initialized &&
      bookmarkState.initialized &&
      roleState.initialized &&
      storyState.initialized &&
      colorsetState.initialized &&
      notificationState.initialized &&
      !!activeUserStore.commune // --> active commune is set by route-context.guard.ts
    );
  }

  /**
   * Handles the layers by determining the type IDs and fetching layers
   * associated with those type IDs for admin users. If the user is not an admin,
   * the method exits without performing any operations. For admin users, it
   * retrieves all type IDs from the layer type store and uses them to find
   * associated layers from the layer store.
   *
   * @return {void} This method does not return a value.
   */
  handleLayers(): void {
    if (
      this.activeUserStore.context() === 'dataBackend' &&
      this.activeUserStore.canSeeAllInWorkspaceType('dataLayerType')
    )
      return; // data gets loaded by layer-store
    const typeIds = this.layerTypeStore.allIds();

    const userWorkspaces = this.activeUserStore.workspaceIds();
    const layerTypeYears = this.buildLayerTypeYearsMap(userWorkspaces);

    if (typeIds && typeIds.length > 0) {
      this.layerStore.findByParentIds(typeIds, layerTypeYears);
    } else {
      this.layerStore.finishEmpty();
    }
  }

  /**
   * Handles the process of managing indicators based on user roles and category IDs.
   * Checks if the current user is an administrator and bypasses further execution if true.
   * Retrieves all category IDs from the category store and, if any are found, uses them to fetch corresponding indicators.
   *
   * @return {void} This method does not return a value.
   */
  handleIndicators(): void {
    if (
      this.activeUserStore.isAdmin() &&
      this.activeUserStore.context() === 'dataBackend'
    )
      return; // find all by store-initialization

    const categoryIds = this.categoryStore.allIds();

    if (this.activeUserStore.context() === 'guestView') {
      const guestConfig = this.activeUserStore.guestAccessConfig();
      if (!guestConfig) return;

      this.indicatorStore.fetchByIds(guestConfig.allowedIndicators);
    } else if (categoryIds.length > 0) {
      this.indicatorStore.findByCategoryIds(categoryIds);
    } else {
      this.indicatorStore.finishEmpty();
    }
  }

  /**
   * Handles the loading and processing of indicator-related data based on access permissions and available data.
   * If the current user has full data access, the processing is skipped as data is pre-loaded. For users with restricted access,
   * it validates required IDs, fetches allowed data layers, and processes the data accordingly.
   *
   * @return {void} This method does not return any value.
   */
  handleIndicatorData(): void {
    if (this.hasFullDataAccess()) {
      return; // data gets loaded by indicator-data-store
    }

    const { dataLayerIds, categoryIds } = this.getIndicatorDataIdGuards();

    if (!this.areRequiredIdsValid(dataLayerIds, categoryIds)) {
      this.indicatorDataStore.finishEmpty();
      return;
    }

    if (this.activeUserStore.context() === 'guestView') {
      this.indicatorStore.allIds();
      const guestConfig = this.activeUserStore.guestAccessConfig();
      this.indicatorDataStore.fetchByIndicatorIds({
        indicatorIds: guestConfig?.allowedIndicators || [],
      });
    } else {
      const userWorkspaces = this.activeUserStore.workspaceIds();
      const mergedTypeGroups =
        this.workspaceStore.mergeTypeGroups(userWorkspaces);

      const layerTypeYears = this.buildLayerTypeYearsMap(userWorkspaces);
      const allowedLayerIds = this.filterLayersByAllowedYears(layerTypeYears);

      this.processIndicatorDataFetch(
        allowedLayerIds,
        categoryIds,
        mergedTypeGroups
      );
    }
  }

  /**
   * Handles loading and management of geographical data based on user access level.
   * If the user has full data access, all geo data is loaded. For restricted access,
   * data is loaded based on the user's workspace permissions and their associated type groups.
   *
   * @return {void} Does not return a value.
   */
  handleGeoData(): void {
    if (this.hasFullDataAccess()) {
      setTimeout(() => this.geoDataStore.loadAllItemsByPromise(), 100);
      return;
    }
    const userWorkspaces = this.activeUserStore.workspaceIds();
    const mergedTypeGroups =
      this.workspaceStore.mergeTypeGroups(userWorkspaces);
    const filterByKeys: string[] = [];

    Object.values(mergedTypeGroups).forEach(keys => filterByKeys.push(...keys));

    this.geoDataStore.findMany({ filterByKeys });
  }

  /**
   * Builds a map of layer type IDs to their allowed years based on user workspaces.
   *
   * @param {string[]} userWorkspaces - Array of workspace IDs
   * @return {Record<string, number[]>} Map of layer type ID to allowed years
   */
  private buildLayerTypeYearsMap(
    userWorkspaces: string[]
  ): Record<string, string[]> {
    const layerTypeYears: Record<string, string[]> = {};
    const workspaces = this.workspaceStore.getByIds(userWorkspaces);

    workspaces.forEach(workspace => {
      const types = this.layerTypeStore.filterByWorkspace(workspace._id);
      types.forEach(type => {
        if (layerTypeYears[type]) {
          layerTypeYears[type].push(...workspace.allowedYears);
        } else {
          layerTypeYears[type] = [...workspace.allowedYears];
        }
      });
    });

    return layerTypeYears;
  }

  /**
   * Filters data layers based on allowed years for their parent layer types.
   * Layers without year restrictions are automatically included.
   *
   * @param {Record<string, number[]>} layerTypeYears - Map of layer type ID to allowed years
   * @return {string[]} Array of allowed layer IDs
   */
  private filterLayersByAllowedYears(
    layerTypeYears: Record<string, string[]>
  ): string[] {
    const allowedLayerIds: string[] = [];
    const dataLayers = this.layerStore.asArray();

    dataLayers.forEach(layer => {
      const hasParentRestriction =
        layer.parent &&
        layerTypeYears[layer.parent] &&
        layerTypeYears[layer.parent].length > 0;

      if (hasParentRestriction) {
        if (layerTypeYears[layer.parent].includes(String(layer.year))) {
          allowedLayerIds.push(layer._id);
        }
      } else {
        allowedLayerIds.push(layer._id);
      }
    });

    return allowedLayerIds;
  }

  /**
   * Checks if the current user has full access to all workspace types and is in data backend context.
   *
   * @return {boolean} True if user has full access, false otherwise.
   */
  private hasFullDataAccess(): boolean {
    return (
      (this.activeUserStore.isAdmin() &&
        this.activeUserStore.context() !== 'atlasInternal') ||
      (this.activeUserStore.canSeeAllInWorkspaceType('indicatorCategory') &&
        this.activeUserStore.canSeeAllInWorkspaceType('dataLayerType') &&
        this.activeUserStore.context() === 'dataBackend')
    );
  }

  /**
   * Retrieves the required data layer and category IDs from their respective stores.
   *
   * @return {object} Object containing dataLayerIds and categoryIds arrays.
   */
  private getIndicatorDataIdGuards(): {
    dataLayerIds: string[];
    categoryIds: string[];
  } {
    return {
      dataLayerIds: this.layerStore.allIds(),
      categoryIds: this.categoryStore.allIds(),
    };
  }

  /**
   * Validates that the required IDs arrays are not empty.
   *
   * @param {string[]} dataLayerIds - Array of data layer IDs
   * @param {string[]} categoryIds - Array of category IDs
   * @return {boolean} True if both arrays have content, false otherwise.
   */
  private areRequiredIdsValid(
    dataLayerIds: string[],
    categoryIds: string[]
  ): boolean {
    return !!(dataLayerIds?.length && categoryIds?.length);
  }

  /**
   * Processes the indicator data fetch based on merged type groups.
   * Either fetches filtered data for each type group or fetches all data if no groups exist.
   *
   * @param {string[]} dataLayerIds - Array of data layer IDs
   * @param {string[]} categoryIds - Array of category IDs
   * @param {Record<string, string[]>} mergedTypeGroups - Merged type groups from workspaces
   * @return {void}
   */
  private processIndicatorDataFetch(
    dataLayerIds: string[],
    categoryIds: string[],
    mergedTypeGroups: Record<string, string[]>
  ): void {
    if (Object.keys(mergedTypeGroups).length > 0) {
      this.fetchFilteredIndicatorData(
        dataLayerIds,
        categoryIds,
        mergedTypeGroups
      );
    } else {
      this.fetchAllIndicatorData(dataLayerIds, categoryIds);
    }
  }

  /**
   * Fetches indicator data filtered by type groups and their associated geo districts.
   *
   * @param {string[]} dataLayerIds - Array of data layer IDs
   * @param {string[]} categoryIds - Array of category IDs
   * @param {Record<string, string[]>} mergedTypeGroups - Merged type groups from workspaces
   * @return {void}
   */
  private fetchFilteredIndicatorData(
    dataLayerIds: string[],
    categoryIds: string[],
    mergedTypeGroups: Record<string, string[]>
  ): void {
    Object.keys(mergedTypeGroups).forEach(typeId => {
      const filterByKeys = this.getFilterKeysByTypeId(
        typeId,
        mergedTypeGroups[typeId]
      );

      this.indicatorDataStore.findMany({
        dataLayerIds,
        categoryIds,
        filterByKeys,
      });
    });
  }

  /**
   * Fetches all indicator data without filtering.
   *
   * @param {string[]} dataLayerIds - Array of data layer IDs
   * @param {string[]} categoryIds - Array of category IDs
   * @return {void}
   */
  private fetchAllIndicatorData(
    dataLayerIds: string[],
    categoryIds: string[]
  ): void {
    this.indicatorDataStore.findMany({ dataLayerIds, categoryIds });
  }

  /**
   * Gets filter keys for a specific type ID by processing geo data and districts.
   *
   * @param {string} typeId - The type ID to process
   * @param {string[]} groupKeys - Array of group keys for this type
   * @return {string[]} Array of filter keys, or undefined if no valid geo data found
   */
  private getFilterKeysByTypeId(
    typeId: string,
    groupKeys: string[]
  ): string[] | undefined {
    const geoId = this.layerStore.getLatestGeoDataIdByParent(typeId);

    if (!geoId) {
      return undefined;
    }

    const districts = this.geoDataStore.getDistrictsById(geoId);

    return districts
      ? getFeatureAndFilterGroups(districts, groupKeys)
      : undefined;
  }

  /**
   * Checks if the current user is an admin with full access in the data backend context.
   *
   * @return {boolean} True if user can see all bookmark categories and is in dataBackend context.
   */
  private isAdminInDataBackend(): boolean {
    return (
      this.activeUserStore.canSeeAllInWorkspaceType('bookmarkCategory') &&
      this.activeUserStore.context() === 'dataBackend'
    );
  }

  /**
   * Handles bookmark functionality by retrieving and processing bookmark categories and their associated bookmarks.
   * Only executes the operation if the active user has admin privileges.
   *
   * @return {void} No return value.
   */
  handleBookmarks(): void {
    // Skip if admin user in dataBackend context - data loads via bookmark-store
    if (this.isAdminInDataBackend()) {
      return;
    }

    if (this.activeUserStore.context() === 'guestView') {
      this.handleGuestViewBookmarks();
    } else {
      this.handleStandardBookmarks();
    }
  }

  /**
   * Handles bookmark loading for guest view context.
   * Loads only the specific bookmark from guest configuration or finishes empty.
   *
   * @return {void}
   */
  private handleGuestViewBookmarks(): void {
    const guestConfig = this.activeUserStore.guestAccessConfig();

    if (this.hasValidGuestBookmark(guestConfig)) {
      this.loadGuestBookmark(guestConfig!);
    } else {
      this.finishBookmarksEmpty();
    }

    // Guest view never shows stories
    this.storyStore.finishEmpty();
  }

  /**
   * Handles bookmark loading for standard (non-guest) context.
   * Loads bookmarks and stories by category IDs or finishes empty if no categories exist.
   *
   * @return {void}
   */
  private handleStandardBookmarks(): void {
    const categoryIds = this.bookmarkCategoryStore.allIds() || [];
    this.bookmarkStore.findByCategoryIds(categoryIds);
    this.storyStore.findByCategoryIds(categoryIds);
  }

  /**
   * Checks if guest configuration contains a valid bookmark.
   *
   * @param {GuestAccessConfig | undefined} config - Guest access configuration
   * @return {boolean} True if config has a bookmark with an entity ID
   */
  private hasValidGuestBookmark(
    config: GuestAccessConfig | undefined
  ): boolean {
    return !!(config?.entity && config.entityId);
  }

  /**
   * Loads a single bookmark for guest view by overwriting the bookmark store.
   *
   * @param {GuestAccessConfig} config - Guest access configuration containing bookmark
   * @return {void}
   */
  private loadGuestBookmark(config: GuestAccessConfig): void {
    const bookmarkItems: { [key: string]: Bookmark } = {
      [config.entityId]: config.entity as Bookmark,
    };

    this.bookmarkStore.overwriteItems(bookmarkItems);
  }

  /**
   * Finishes both bookmark and story stores as empty.
   *
   * @return {void}
   */
  private finishBookmarksEmpty(): void {
    this.bookmarkStore.finishEmpty();
    this.storyStore.finishEmpty();
  }

  /**
   * Abstract method to enforce custom initialization logic
   */
  abstract performInitialization(): void;
}
